<script setup lang="ts">
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.css'
import categoryService from '../services/category.service'
import clubApplicationService from '../services/clubApplication.service'
import { useToastStore } from '../stores/toast'

const props = defineProps<{
  show: boolean
  clubUuid: string
  applicationUuids: string[]
  notActivatedCount: number
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'done'): void
}>()

const { t } = useI18n()
const toastStore = useToastStore()

const categories = ref<Array<{ uuid: string; name: string }>>([])
const selectedCategories = ref<Array<{ uuid: string; name: string }>>([])
const loadingCategories = ref(false)
const submitting = ref(false)

const fetchCategories = async () => {
  if (!props.clubUuid) return
  loadingCategories.value = true
  try {
    categories.value = await categoryService.getCategoriesForFilters(props.clubUuid)
  } catch (err) {
    console.error('Fetch categories error:', err)
  } finally {
    loadingCategories.value = false
  }
}

watch(() => props.show, (visible) => {
  if (visible) {
    selectedCategories.value = []
    if (categories.value.length === 0) fetchCategories()
  }
})

const close = () => {
  if (!submitting.value) emit('close')
}

const handleAccept = async () => {
  submitting.value = true
  try {
    const response = await clubApplicationService.accept(
      props.clubUuid,
      props.applicationUuids,
      selectedCategories.value.map(c => c.uuid)
    )
    toastStore.success(response.message || t('clubApplications.acceptedSuccess'))
    emit('done')
  } catch (err: any) {
    console.error('Accept applications error:', err)
    toastStore.error(err.response?.data?.message || t('clubApplications.errorAccepting'))
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="close"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-2">{{ t('clubApplications.selectCategoriesTitle') }}</h2>
        <p class="text-sm text-gray-600 mb-4">
          {{ t('clubApplications.acceptCount', { count: applicationUuids.length }) }}
        </p>

        <div
          v-if="notActivatedCount > 0"
          class="mb-4 p-3 bg-amber-50 border-2 border-amber-300 rounded text-sm text-amber-800"
        >
          {{ t('clubApplications.notActivatedWarning', { count: notActivatedCount }) }}
        </div>

        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ t('clubApplications.categoriesOptional') }}
          </label>
          <Multiselect
            v-model="selectedCategories"
            :options="categories"
            :multiple="true"
            :close-on-select="false"
            :clear-on-select="false"
            :preserve-search="true"
            :placeholder="t('players.selectCategories')"
            label="name"
            track-by="uuid"
            :loading="loadingCategories"
          />
          <p class="mt-1 text-xs text-gray-500">{{ t('clubApplications.categoriesHint') }}</p>
        </div>

        <div class="flex gap-3 justify-end">
          <button
            @click="close"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="submitting"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleAccept"
            class="px-4 py-2 bg-green-600 text-sm font-medium text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="submitting || applicationUuids.length === 0"
          >
            {{ submitting ? t('clubApplications.accepting') : t('clubApplications.accept') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
